export class CryptoProfileError extends Error {
  public readonly code: string;

  public constructor(code: string, message: string) {
    super(message);
    this.name = "CryptoProfileError";
    this.code = code;
  }
}

export function fail(code: string, message: string): never {
  throw new CryptoProfileError(code, message);
}

export function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return fail("INVALID_FORMAT", `${label} must be an object`);
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return fail("INVALID_FORMAT", `${label} must be a plain object`);
  }

  return value as Record<string, unknown>;
}

export function assertExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
  label: string,
): void {
  const actual = Object.keys(value).sort();
  const wanted = [...expected].sort();
  if (
    actual.length !== wanted.length ||
    actual.some((key, index) => key !== wanted[index])
  ) {
    fail("INVALID_FORMAT", `${label} has unexpected or missing fields`);
  }
}

export function readString(
  value: Record<string, unknown>,
  key: string,
  label: string,
  maximumLength = 1024,
): string {
  const result = value[key];
  if (
    typeof result !== "string" ||
    result.length === 0 ||
    result.length > maximumLength
  ) {
    return fail("INVALID_FORMAT", `${label}.${key} is invalid`);
  }
  return result;
}

export function readInteger(
  value: Record<string, unknown>,
  key: string,
  label: string,
  minimum = 0,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  const result = value[key];
  if (
    typeof result !== "number" ||
    !Number.isSafeInteger(result) ||
    result < minimum ||
    result > maximum
  ) {
    return fail("INVALID_FORMAT", `${label}.${key} is invalid`);
  }
  return result;
}

export function readArray(
  value: Record<string, unknown>,
  key: string,
  label: string,
  maximumLength = 1000,
): unknown[] {
  const result = value[key];
  if (!Array.isArray(result) || result.length > maximumLength) {
    return fail("INVALID_FORMAT", `${label}.${key} is invalid`);
  }
  return result;
}

export function assertLiteral<T extends string | number | boolean>(
  actual: unknown,
  expected: T,
  label: string,
): asserts actual is T {
  if (actual !== expected) {
    fail("INVALID_FORMAT", `${label} is unsupported`);
  }
}

export function assertSafeIdentifier(value: string, label: string): void {
  if (!/^[A-Za-z0-9._~:-]{1,256}$/u.test(value)) {
    fail("INVALID_FORMAT", `${label} is invalid`);
  }
}

export function assertHttpsOrigin(value: string, label: string): void {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return fail("INVALID_FORMAT", `${label} must be an HTTPS origin`);
  }

  if (
    (url.protocol !== "https:" &&
      !(
        url.protocol === "http:" &&
        (url.hostname === "127.0.0.1" || url.hostname === "localhost")
      )) ||
    url.origin !== value ||
    url.username !== "" ||
    url.password !== ""
  ) {
    fail("INVALID_FORMAT", `${label} must be an exact HTTPS origin`);
  }
}
